import React, { useCallback, useMemo } from 'react';

import { CatalogRoom, ConflictInfo, ConflictResolutionStatus, StopsellRoom } from '~/types/stopsell';

import { conflictKey } from './StopsellReviewModal';
import StopsellRoomGroup from './StopsellRoomGroup';

interface Props {
  roomIndex: number;
  room: StopsellRoom;
  selected: Array<boolean>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  roomConflicts: Array<Array<ConflictInfo>>;
  resolutions: Record<string, ConflictResolutionStatus>;
  onDateChange: (roomIndex: number, dateIndex: number, value: boolean) => void;
  onToggleAll: (roomIndex: number, value: boolean) => void;
  onRoomTypeChange: (roomIndex: number, roomTypeId: string) => void;
  onResolve: (roomIndex: number, dateIndex: number, conflictIndex: number, status: ConflictResolutionStatus) => void;
  onUndo: (roomIndex: number, dateIndex: number, conflictIndex: number) => void;
}

export default function StopsellRoomGroupRow({
  roomIndex,
  room,
  selected,
  catalogRooms,
  selectedRoomTypeId,
  roomConflicts,
  resolutions,
  onDateChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
}: Props) {
  const blackoutDates = useMemo(
    () => catalogRooms.find((c) => c.id === selectedRoomTypeId)?.blackoutDates ?? [],
    [catalogRooms, selectedRoomTypeId],
  );

  const conflictResolutions = useMemo(
    () =>
      roomConflicts.map((dateConflicts, dateIndex) =>
        dateConflicts.map((_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null),
      ),
    [roomConflicts, resolutions, roomIndex],
  );

  const handleChange = useCallback(
    (dateIndex: number, value: boolean) => onDateChange(roomIndex, dateIndex, value),
    [roomIndex, onDateChange],
  );

  const handleToggleAll = useCallback((value: boolean) => onToggleAll(roomIndex, value), [roomIndex, onToggleAll]);

  const handleRoomTypeChange = useCallback(
    (roomTypeId: string) => onRoomTypeChange(roomIndex, roomTypeId),
    [roomIndex, onRoomTypeChange],
  );

  const handleResolve = useCallback(
    (dateIndex: number, conflictIndex: number, status: ConflictResolutionStatus) =>
      onResolve(roomIndex, dateIndex, conflictIndex, status),
    [roomIndex, onResolve],
  );

  const handleUndo = useCallback(
    (dateIndex: number, conflictIndex: number) => onUndo(roomIndex, dateIndex, conflictIndex),
    [roomIndex, onUndo],
  );

  return (
    <StopsellRoomGroup
      room={room}
      selected={selected}
      catalogRooms={catalogRooms}
      selectedRoomTypeId={selectedRoomTypeId}
      blackoutDates={blackoutDates}
      conflicts={roomConflicts}
      conflictResolutions={conflictResolutions}
      onChange={handleChange}
      onToggleAll={handleToggleAll}
      onRoomTypeChange={handleRoomTypeChange}
      onResolve={handleResolve}
      onUndo={handleUndo}
    />
  );
}
